import Dropdown, { MultiDropdown, Option } from "@components/form/Dropdown.component";
import DateRangeSelector from "@components/form/DateRangeSelector.component";
import { AnalysisDimensionFilter } from "@models/analysis";
import { DashboardDateRange, DashboardFilterOption } from "@models/dashboard";
import { Filter, PipelineNodeField } from "@models/pipelineNode";
import { useDimensions, useUniqueValuesInColumn } from "@models/reportBuilder";
import SingleDimensionFilter from "@pages/Analysis/SingleDimensionFilter.component";
import { comparatorOptions } from "@pages/SourceRecordType/FilterConfigForm.component";
import { useAvailableSnowflakeTables, useColumnValueDistribution, usePipelineNode, usePipelineNodes } from "@stores/data.store";
import moment from "moment";
import { node } from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { Updater } from "use-immer";
import { useDebounce } from "use-debounce";

interface Props {
    option: DashboardFilterOption;
    onChange: (newData: DashboardFilterOption) => any;
    columnOptions: Option[];
}

const DashboardFilterOptionForm = (props: Props) => {
    const changeValue = useCallback((key: keyof DashboardFilterOption, val: any) => {
        const newData = {
            ...props.option,
            [key]: val,
        };

        if (newData.column_references && newData.column_references.length > 0 && newData.filter_type == 'QUERY_LIST' && (!newData.query_list_node_id || !newData.query_list_field_id)) {
            newData.query_list_node_id = newData.column_references[0].pipeline_node_id;
            newData.query_list_field_id = newData.column_references[0].field_id;
        }
        props.onChange(newData);
    }, [props.onChange, props.option]);

    const dimensions = useDimensions();
    
    // We don't want to show date dimensions here so we filter them out. First need to identify the single date dim node
    const dateDimNodes = usePipelineNodes({
        node_type: 'DATE_DIMENSION'
    });

    const dimensionOptions = useMemo(() => {
        if (!dimensions.data) {
            return [];
        }
        return dimensions.data.filter(d => !dateDimNodes.data?.map(n => n.id as string).includes(d.pipeline_node_id)).map(d => ({
            value: d.id as string,
            label: d.name,
        }));
    }, [dimensions.dataUpdatedAt, dateDimNodes.dataUpdatedAt]);

    return <>
        <Form.Group className="mb-3">
            <Form.Label>
                Filter Type
            </Form.Label>
            <Dropdown
                options={
                    [
                        {
                            value: 'DIMENSION',
                            label: 'Smart Filter',
                            description: 'Automatically filters and recalculates all dynamic analyses with a given dimension'
                        },
                        {
                            value: 'QUERY_LIST',
                            label: 'Static Filter',
                            description: 'Filter nodes and static analyses',
                        }, 
                    ]
                }
                selected={props.option.filter_type}
                onChange={(newVal: string) => {
                    changeValue('filter_type', newVal);
                }}
            />
        </Form.Group>
        {props.option.filter_type == 'DIMENSION' && <>
            <Form.Group className="mb-3">
                <Form.Label>
                    Which dimension should this apply to?
                </Form.Label>
                <Dropdown
                    options={dimensionOptions}
                    selected={props.option.dimension_id}
                    onChange={(newVal: string) => {
                        changeValue('dimension_id', newVal);
                    }}
                />
            </Form.Group>
        </>}
        
        {!['DIMENSION'].includes(props.option.filter_type) && <>
            <Form.Group className="mb-3">
                <Form.Label>
                    Which columns should this apply to?
                </Form.Label>
                <MultiDropdown
                    options={props.columnOptions}
                    selected={(props.option.column_references || []).map(cr => {
                        return `${cr.pipeline_node_id}.${cr.field_id}`
                    })}
                    onChange={(newVals: string[]) => {
                        changeValue('column_references', newVals.map(nv => {
                            const spl = nv.split('.');
                            return {
                                pipeline_node_id: spl[0],
                                field_id: spl[1],
                            }
                        }));
                    }}
                />
                <Form.Text>Select all columns you want this filter to apply to. The filter will automatically apply to any visualization or table that uses your selected columns.</Form.Text>
            </Form.Group>
        </>}
        
        
        {props.option.filter_type === 'FREE_TEXT' && <>
            <Form.Group className="mb-2">
                <Form.Label>
                    Comparator
                </Form.Label>
                <Dropdown options={comparatorOptions}
                    selected={props.option.comparator}
                    onChange={(newVal: string) => {
                        changeValue('comparator', newVal);
                    }}
                />

            </Form.Group>
        </>}
        {['STATIC_LIST', 'QUERY_LIST'].includes(props.option.filter_type) &&
             <Form.Group className="mb-2">
                <Form.Check
                    checked={props.option.allow_multiple}
                    onChange={() => {
                        changeValue('allow_multiple', !props.option.allow_multiple)
                    }}
                    label="Allow user to select multiple options from the list"
                />
            </Form.Group>
        }

        {props.option.filter_type === 'QUERY_LIST' && <>
            <Form.Group className="mb-2">
                <Form.Label>Select column for dynamic list</Form.Label>
                <Dropdown
                    options={props.columnOptions}
                    selected={`${props.option.query_list_node_id}.${props.option.query_list_field_id}`}
                    onChange={(newVals: string) => {
                        const spl = newVals.split('.');
                        props.onChange({
                            ...props.option,
                            query_list_node_id: spl[0],
                            query_list_field_id: spl[1],
                        })
                        
                    }}
                />
                <Form.Text>Choose the node and column that Pliable should use to load the dynamic list of options.</Form.Text>
            </Form.Group>
            {/* <Form.Group className="mb-2">
                <Form.Label>
                    Default Value
                </Form.Label>
                <Dropdown
                    options={
                        [
                            {
                                value: 'TODAYS_DATE',
                                label: 'Today\'s Date'
                            }, {
                                value: 'YESTERDAYS_DATE',
                                label: 'Yesterday\'s Date'
                            }
                        ]
                    }
                    selected={props.option.default_value}
                    onChange={(newVal: string) => {
                        changeValue('default_value', newVal);
                    }}
                />
            </Form.Group> */}
        </>}        
    </>
}

interface NodeFieldsById {
    [nodeId: string]: {
        [fieldId: string]: PipelineNodeField;
    }
}

export default DashboardFilterOptionForm;

export interface FiltersByNodeId {
    [nodeId: string]: Filter[]
}

interface FilterFormProps {
    filter_option: DashboardFilterOption;
    onChange: (filtersByNodeId: FiltersByNodeId, dimensionFilters?: AnalysisDimensionFilter[], dateRange?: DashboardDateRange) => any;
}

const QueryListFilterForm = (props: FilterFormProps) => {
    const pipelineNodes = usePipelineNodes();
    const [query, setQuery] = useState<string>('');
    const [debouncedQuery] = useDebounce(query, 2000);

    const [value, setValue] = useState<string[]>([]);
    const availableValues = useUniqueValuesInColumn(props.filter_option.query_list_node_id || '', props.filter_option.query_list_field_id || '');
    
    useEffect(() => {
        if (!pipelineNodes.data) {
            return;
        }
        
        if (props.filter_option.default_value) {
            switch (props.filter_option.default_value) {
                case 'TODAYS_DATE':
                    const todaysDate = moment().format('YYYY-MM-DD');
                    setValue([todaysDate]);
                    handleChange([todaysDate]);
                    break;
                case 'YESTERDAYS_DATE':
                    const yesterdaysDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
                    setValue([yesterdaysDate]);
                    handleChange([yesterdaysDate]);
                    break;
            }
        }
    },[props.filter_option.default_value, pipelineNodes.dataUpdatedAt])

    const filterOptions = useMemo(() => {
        if (!availableValues.data) {
            return [];
        }

        return availableValues.data
            .filter(v => !!v)
            .map(v => ({
                value: v,
                label: v,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [availableValues.dataUpdatedAt]);

    const nodeFieldsById: NodeFieldsById = useMemo(() => {
        if (!pipelineNodes.data) {
            return {};
        }
        const nodeFieldsById: NodeFieldsById = {};

        pipelineNodes.data.forEach(pn => {
            nodeFieldsById[pn.id as string] = {};
            pn.fields.forEach(f => {
                nodeFieldsById[pn.id as string][f.id] = f;
            });
        });
        return nodeFieldsById
    }, [pipelineNodes.dataUpdatedAt]);


    const handleChange = useCallback((newVals: string[]) =>  {
        
        setValue(newVals);
        if (!pipelineNodes.data) {
            return;
        }

        if (props.filter_option.filter_type == 'DIMENSION') {
            props.onChange({}, [{
                dimension_id: props.filter_option.dimension_id || '',
                comparator: 'IN',
                value: newVals,
            }]);
            return;
        }
        const filtersByNodeId: FiltersByNodeId = {};
        props.filter_option.column_references?.forEach(cr => {
            if (!(cr.pipeline_node_id in filtersByNodeId)) {
                filtersByNodeId[cr.pipeline_node_id] = [];
            }

            try {
                const field = nodeFieldsById[cr.pipeline_node_id][cr.field_id];

                filtersByNodeId[cr.pipeline_node_id].push({
                    comparator: 'IN',
                    value: newVals,
                    column_ref: {
                        column_path: field.name,
                    }
                });
            } catch (err) {
                console.error(err);
            }
            
        });

        props.onChange(filtersByNodeId);

    }, [props.onChange, pipelineNodes.dataUpdatedAt]);

    if (props.filter_option.allow_multiple) {
        return <div className="d-flex center-vertically w-100">
            <MultiDropdown
                className="flex-1"
                selected={value}
                onChange={handleChange}
                options={filterOptions}
                onInputChange={(newQuery) => setQuery(newQuery)}
            />
            {availableValues.isLoading && <i className="mdi mdi-loading mdi-spin ms-2" />}
            {!availableValues.isLoading && <i className="mdi mdi-check ms-2" />}
        </div>
    }
    return <>
        <Dropdown
            selected={value.length > 0 ? value[0] : ''}
            options={filterOptions}
            onChange={(newVal: string) => {
                handleChange([newVal])
            }}
        />
    </>
}


const DimensionFilterForm = (props: FilterFormProps) => {
    const dimensions = useDimensions();
    const [value, setValue] = useState<string[]>(() => {
        // Initialize from default_value if present
        if (props.filter_option.default_value) {
            return Array.isArray(props.filter_option.default_value)
                ? props.filter_option.default_value
                : [props.filter_option.default_value];
        }
        return [];
    });

    // Ensure value is synced with default_value on mount or when default_value changes
    useEffect(() => {
        if (props.filter_option.default_value) {
            const defaultVal = Array.isArray(props.filter_option.default_value)
                ? props.filter_option.default_value
                : [props.filter_option.default_value];
            setValue(defaultVal);
            // Trigger onChange so the filter is applied
            props.onChange({}, [{
                dimension_id: props.filter_option.dimension_id || '',
                comparator: 'IN',
                value: defaultVal,
            }]);
        }
    }, [props.filter_option.default_value, props.filter_option.dimension_id]);

    const handleChange = useCallback((newVals: string[]) => {
        setValue(newVals);
        props.onChange({}, [{
            dimension_id: props.filter_option.dimension_id || '',
            comparator: 'IN',
            value: newVals,
        }]);
    }, [props.onChange, props.filter_option.dimension_id]);

    return <>
        <SingleDimensionFilter
            displayType='form'
            dimensionFilter={{
                dimension_id: props.filter_option.dimension_id || '',
                comparator: 'IN',
                value: value,
            }}
            onChange={(newFilter) => {
                handleChange(newFilter.value as string[]);
            }}
        />
    </>;
}



export const FilterForm = (props: FilterFormProps) => {
    if (props.filter_option.filter_type === 'QUERY_LIST') {
        return <QueryListFilterForm {...props} />;
    }
    if (props.filter_option.filter_type === 'DIMENSION') {
        return <DimensionFilterForm {...props} />;
    }
    return <></>;
}
